import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { api } from '../api/client'
import type { Sensor, SensorDoc } from '../api/types'
import { sizeLabel } from '../lib/projectChat'
import {
  alreadyHas, attachLabel, changeLabel, changeNote, diff, familyOf, filterSensors, groupByStation,
  isUnfolded, refOf, selectShown, stationsSummary, toggleKey, type PickerFilter,
} from '../lib/projectFiles'
import { useMovable } from '../lib/useMovable'
import { Chevron } from './Chevron'
import { FileBadge, UploadList, useUploads, type Sent } from './ProjectDocuments'
import './documents.css'

export type PickerMode = { kind: 'new' } | { kind: 'change'; doc: SensorDoc }

const message = (failed: unknown) => (failed instanceof Error ? failed.message : 'Something went wrong.')
const NONE: PickerFilter = { query: '', station: '', type: '' }
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

function attachedNote(attached: number, already: number): string {
  const sent = `Attached to ${attached} sensor${attached === 1 ? '' : 's'}`
  return already ? `${sent}; ${already} already had it` : sent
}

/** A tick that can say "some": a station whose sensors are partly chosen. */
function TriBox({ state, label, disabled, onChange }: {
  state: 'none' | 'some' | 'all'; label: string; disabled: boolean; onChange: () => void
}) {
  return (
    <input type="checkbox" aria-label={label} checked={state === 'all'} disabled={disabled}
           aria-checked={state === 'some' ? 'mixed' : state === 'all'}
           ref={(box) => { if (box) box.indeterminate = state === 'some' }} onChange={onChange} />
  )
}

/** Upload to sensors (screen 3 of the design): choose the files, then
 *  exactly which sensors of the project's stations get them; or, for a
 *  file already on sensors, add it to more or take it off some. The server
 *  attaches each file to every sensor chosen, or to none. */
export function SensorPicker({ projectId, mode, sensors: given, docs: givenDocs, onDone, onClose }: {
  projectId: number; mode: PickerMode; sensors: Sensor[]; docs: SensorDoc[]
  onDone: () => void; onClose: () => void
}) {
  // The lists as the dialog opened: the tab reloads after a change, and the
  // summary must not read "0 sensors" beside "Attached to 100 sensors".
  const [sensors] = useState(given)
  const [docs] = useState(givenDocs)
  const initial = useMemo(() => new Set(mode.kind === 'change' ? mode.doc.sensors.map((s) => s.ref) : []),
                          [mode])
  const [chosen, setChosen] = useState<Set<string>>(initial)
  const [filter, setFilter] = useState<PickerFilter>(NONE)
  // Stations start folded: a project of several gateways lists hundreds of
  // sensors, and the station tick chooses all of one without opening it.
  const [opened, setOpened] = useState<Set<string>>(new Set())
  const [files, setFiles] = useState<File[]>([])
  const [phase, setPhase] = useState<'picking' | 'sending' | 'done'>('picking')
  const [result, setResult] = useState<string>()
  const [error, setError] = useState<string>()
  const { uploads, run } = useUploads()
  const movable = useMovable()
  const dialog = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)
  const outcome = useRef<HTMLDivElement>(null)
  const sendingNow = useRef(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const close = useRef(onClose)
  close.current = onClose
  const sending = phase === 'sending'

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    search.current?.focus()
    // Esc is heard on the document: while a file is on its way the focused
    // control is disabled, and a key on the dialog alone would go unheard.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !sendingNow.current) close.current()
    }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); opener?.focus?.() }
  }, [])
  sendingNow.current = sending
  // Focus follows the work: to the results while sending, so it is never
  // left on a control that has just been disabled, and on to Done.
  useEffect(() => {
    if (phase !== 'picking') outcome.current?.focus()
  }, [phase])

  const shown = filterSensors(sensors, filter)
  const groups = groupByStation(shown, chosen)
  const summary = stationsSummary(chosen, sensors)
  const change = diff(initial, chosen)
  const one = files.length === 1 ? files[0]! : null
  const holds = (ref: string) => (mode.kind === 'new' && one ? alreadyHas(docs, ref, one) : false)
  const stations = useMemo(() => [...new Map(sensors.map((s) => [s.station.code, s.station.name]))], [sensors])
  const types = useMemo(() => [...new Set(sensors.map((s) => s.sensor_type).filter((t) => t !== null))].sort(),
                        [sensors])
  const tick = (list: Sensor[], on: boolean) =>
    setChosen((now) => selectShown(now, on ? list.filter((s) => !holds(refOf(s))) : list, on))
  const set = (patch: Partial<PickerFilter>) => setFilter((f) => ({ ...f, ...patch }))

  const ready = mode.kind === 'new' ? files.length > 0 && summary.count > 0
    : change.add.length + change.remove.length > 0
  const label = mode.kind === 'new' ? attachLabel(summary.count)
    : changeLabel(change.add.length, change.remove.length)

  const submit = async () => {
    setError(undefined)
    setPhase('sending')
    if (mode.kind === 'new') {
      const refs = [...chosen]
      await run(files, async (file, onProgress): Promise<Sent> => {
        const r = await api.projects.attachSensorDoc(projectId, file, refs, onProgress)
        const refused = r.refused[0]?.reason
        return refused ? { refused } : { note: attachedNote(r.attached, r.already) }
      })
      setPhase('done')
      onDone()
      return
    }
    try {
      const r = await api.projects.changeSensorDoc(projectId, mode.doc.key, change)
      setResult(changeNote(r.added, r.removed, r.already))
      setPhase('done')
      onDone()
    } catch (failed) {
      setError(message(failed))
      setPhase('picking')
    }
  }

  // Tab stays inside the window (Esc is heard on the document, above).
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialog.current) return
    const items = [...dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null)
    if (items.length === 0) return
    const [first, last] = [items[0]!, items[items.length - 1]!]
    // From the results line, or anything else not in the list, Tab goes to
    // an end of the window rather than to the page behind it.
    if (!items.includes(document.activeElement as HTMLElement)) {
      e.preventDefault(); (e.shiftKey ? last : first).focus()
    } else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }

  const title = mode.kind === 'new' ? 'Upload to sensors' : 'Sensors holding this file'
  return (
    <div className="dialog-backdrop">
      <div ref={dialog} className="dialog sp-dialog" role="dialog" aria-modal="true" aria-labelledby="sp-title"
           style={movable.style} onKeyDown={onKeyDown}>
        <div className="dialog-head">
          <button type="button" className="icon-button small grip" aria-label="Move the window" {...movable.grip}>
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              {[6, 12, 18].flatMap((y) => [9, 15].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
            </svg>
          </button>
          <div className="dialog-title" {...movable.handle}>
            <h2 id="sp-title">{title}</h2>
            {mode.kind === 'change' && <p className="muted small">{mode.doc.name}</p>}
          </div>
          <button type="button" className="icon-button plain" aria-label="Close" disabled={sending} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </button>
        </div>

        <div className="sp-body">
          <section className="sp-left" aria-label="Choose sensors">
            <div className="sp-filters">
              <label className="field inline sp-search">
                <span className="visually-hidden">Search sensors</span>
                <input ref={search} type="search" placeholder="Search sensors" value={filter.query}
                       disabled={phase !== 'picking'} onChange={(e) => set({ query: e.target.value })} />
              </label>
              <label className="field inline">
                <span className="visually-hidden">Station</span>
                <select value={filter.station} disabled={phase !== 'picking'} onChange={(e) => set({ station: e.target.value })}>
                  <option value="">All stations</option>
                  {stations.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
              </label>
              <label className="field inline">
                <span className="visually-hidden">Sensor type</span>
                <select value={filter.type} disabled={phase !== 'picking'} onChange={(e) => set({ type: e.target.value })}>
                  <option value="">All types</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                  <option value="unset">Not set</option>
                </select>
              </label>
            </div>
            <div className="sp-bulk">
              <span className="muted" role="status">{shown.length} sensor{shown.length === 1 ? '' : 's'} match.</span>
              <button type="button" className="button small" disabled={phase !== 'picking' || shown.length === 0}
                      onClick={() => tick(shown, true)}>Select all {shown.length}</button>
              <button type="button" className="button small" disabled={phase !== 'picking' || shown.length === 0}
                      onClick={() => tick(shown, false)}>Clear</button>
              <span className="sp-bulk-folds">
                <button type="button" className="sp-action" disabled={groups.length === 0}
                        onClick={() => setOpened(new Set(groups.map((g) => g.code)))}>Open all</button>
                <button type="button" className="sp-action" disabled={groups.length === 0}
                        onClick={() => setOpened(new Set())}>Close all</button>
              </span>
              {mode.kind === 'change' && (
                <button type="button" className="link danger" disabled={phase !== 'picking'}
                        onClick={() => setChosen(new Set())}>Take it off every sensor</button>
              )}
            </div>
            <div className="sp-list">
              {groups.length === 0 && <p className="muted sp-pad">No sensor matches.</p>}
              {groups.map((g) => {
                const open = isUnfolded(g.code, filter.query, opened)
                const listId = `sp-list-${g.code}`
                return (
                <fieldset key={g.code} className="sp-group" disabled={phase !== 'picking'}>
                  <legend className="sp-group-head">
                    <TriBox state={g.state} label={`All of ${g.name}`} disabled={phase !== 'picking'}
                            onChange={() => tick(g.sensors, g.state !== 'all')} />
                    <button type="button" className="sp-fold" aria-expanded={open} aria-controls={listId}
                            onClick={() => setOpened((now) => toggleKey(now, g.code))}>
                      <Chevron />
                      <span className="sp-station">{g.name}</span>
                      <span className="explore-count">
                        {g.selected} of {g.sensors.length} selected
                      </span>
                    </button>
                  </legend>
                  <div id={listId} hidden={!open}>
                  {open && g.sensors.map((s) => {
                    const ref = refOf(s)
                    const on = chosen.has(ref)
                    return (
                      <label key={ref} className={`sp-row${on ? ' is-on' : ''}`}>
                        <input type="checkbox" checked={on}
                               onChange={() => setChosen((now) => selectShown(now, [s], !on))} />
                        <span className="sp-row-name">{s.name}</span>
                        <span className="muted sp-row-meta">
                          {s.sensor_type}
                          {holds(ref) && <span className="sp-has">{s.sensor_type ? ' · ' : ''}already has this file</span>}
                        </span>
                      </label>
                    )
                  })}
                  </div>
                </fieldset>
                )
              })}
            </div>
          </section>

          <aside className="sp-right" aria-label="Summary">
            <h3 className="sp-cap">File{mode.kind === 'new' && files.length > 1 ? 's' : ''}</h3>
            {mode.kind === 'change' && (
              <div className="sp-file"><FileBadge family={mode.doc.family} />{mode.doc.name}
                <span className="muted"> · {sizeLabel(mode.doc.bytes)}</span></div>
            )}
            {mode.kind === 'new' && files.map((f) => (
              <div key={`${f.name}-${f.size}`} className="sp-file"><FileBadge family={familyOf(f.name)} />{f.name}
                <span className="muted"> · {sizeLabel(f.size)}</span></div>
            ))}
            {mode.kind === 'new' && phase === 'picking' && (
              <button type="button" className={files.length ? 'link' : 'button'} onClick={() => fileInput.current?.click()}>
                {files.length ? 'Choose other files' : 'Choose files'}
              </button>
            )}
            <input ref={fileInput} type="file" multiple hidden
                   onChange={(e) => { setFiles(Array.from(e.target.files ?? [])); e.target.value = '' }} />

            <h3 className="sp-cap">{mode.kind === 'new' ? 'Going to' : 'Held by'}</h3>
            <p className="sp-count" aria-live="polite">{summary.count} sensor{summary.count === 1 ? '' : 's'}</p>
            <p className="muted">at {summary.stations.length} station{summary.stations.length === 1 ? '' : 's'}</p>
            <div className="sp-chips">
              {summary.stations.map((st) => <span key={st.name} className="sp-chip">{st.name} {st.n}</span>)}
            </div>
            <p className="muted small sp-note">Each sensor's Sensor info will list it. Stored once.</p>
          </aside>
        </div>

        <div ref={outcome} tabIndex={-1} className="sp-outcome" aria-live="polite">
          <UploadList uploads={uploads} />
          {result && <p className="sp-result">{result}</p>}
        </div>
        {error && <p className="error dialog-error" role="alert">{error}</p>}

        <div className="dialog-foot">
          <p className="muted small">Only sensors on this project's stations are listed.</p>
          {phase === 'done' ? (
            <button type="button" className="button primary" onClick={onClose}>Done</button>
          ) : (
            <>
              <button type="button" className="button" disabled={sending} onClick={onClose}>Cancel</button>
              <button type="button" className="button primary" disabled={!ready || sending}
                      onClick={() => void submit()}>{sending ? 'Sending…' : label}</button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
